"use client";

import { useAuthentication } from "@app/providers/AuthenticationProvider/AuthenticationProvider";
import {
  Button,
  IconAlertExclamationFill,
  TextBody,
  TextTitle,
  useToast,
} from "@sourceful/design-system-v3";
import { ProductMain, SanityProductPreset, UtmParam } from "@sourceful/shared-types";
import {
  FormNames,
  SanityForm,
  SanityFormDropdownField,
  SanityFormTextField,
} from "@sourceful/shared-utils/sanity-utils";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { apiConfig } from "apiConfig";
import { AxiosError } from "axios";
import { useRouter, useSearchParams } from "next/navigation";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { usePageContext } from "../../providers/PageProvider/PageProvider";
import SanityFormDropdown from "../sanity/forms/SanityFormDropdown";
import SanityFormText from "../sanity/forms/SanityFormText";
import LeadGenConsentCheckbox from "./LeadGenConsentCheckbox";
import LeadGenFormFileUploader from "./LeadGenFormFileUploader";
import { LeadGenField } from "./lead-gen-types";
import { useLeadGenFormSubmission } from "./useLeadGenFormSubmission";

const RECAPTCHA_SITE_KEY = apiConfig.recaptchaSiteKey;

export interface ProductDetails {
  product: ProductMain;
  preset?: SanityProductPreset;
  quantity?: string;
  size?: string;
}
export interface LeadGenFormProps {
  productDetails: ProductDetails;
  form: SanityForm;
  previewMode?: boolean;
  isCustomSize?: boolean;
}

function isAxiosError(err: any): err is AxiosError {
  return !!err.response?.status;
}

export const extractErrorMessage = (err: any, defaultMessage: string) => {
  return (
    (err.response?.data as { message: string })?.message ??
    (err.response?.data as { errors: Array<{ message: string }> })?.errors?.[0]?.message ??
    defaultMessage
  );
};

const DEFAULT_MSG = "Your form had errors";
const TEXT_ERROR = "Please provide a value";

export enum sanityFieldTypes {
  GROUP = "formGroup",
  TEXT = "formText",
  LIST = "formDropdown",
}

export default function LeadGenForm({
  form,
  productDetails,
  previewMode = false,
  isCustomSize,
}: Readonly<LeadGenFormProps>) {
  const searchParams = useSearchParams();
  const { showExitDialog, setShowExitDialog } = usePageContext();
  const { user, metadata, organisation } = useAuthentication();

  const defaultValues: Record<LeadGenField & UtmParam, string | undefined> = {
    [LeadGenField.NAME]: user?.family_name
      ? `${user?.given_name} ${user?.family_name}`
      : user?.given_name,
    [LeadGenField.BUSINESS_EMAIL]: user?.email,
    [LeadGenField.PHONE]: metadata?.phone_number,
    [LeadGenField.COMPANY_NAME]: organisation?.display_name,
    [LeadGenField.PACKAGING_UNITS_NEEDED_PER_YEAR]: metadata?.packaging_needs,
    [LeadGenField.PRODUCT_ID]: productDetails?.product?.baseProductId?.toString(),
    [LeadGenField.PRODUCT_VERSION_ID]: productDetails?.product?.versionId?.toString(),
    [LeadGenField.PRODUCT]: productDetails?.product?.name,
    [LeadGenField.PRESET]: productDetails?.preset?.name,
    [LeadGenField.QUANTITY]: productDetails?.quantity?.toString(),
    [LeadGenField.SIZE]: productDetails?.size?.toString(),
    [LeadGenField.CUSTOM_SIZE]: isCustomSize ? "yes" : "no",
    [UtmParam.Campaign]: searchParams?.get(UtmParam.Campaign),
    [UtmParam.Content]: searchParams?.get(UtmParam.Content),
    [UtmParam.Medium]: searchParams?.get(UtmParam.Medium),
    [UtmParam.Source]: searchParams?.get(UtmParam.Source),
    [UtmParam.Term]: searchParams?.get(UtmParam.Term),
  };

  const paramsWithValues = Object.values(UtmParam).reduce<[string, string][]>((acc, param) => {
    const value = searchParams?.get(param);
    if (value) acc.push([param, value]);
    return acc;
  }, []);

  const [files, setFiles] = useState<File[] | null>(null);

  const { postFormAndFiles } = useLeadGenFormSubmission();
  const router = useRouter();
  const { toast } = useToast();

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      // discard `files` because it points to the last selected File
      // what we really want is a FileList that holds all selected files
      const { files: _, ...submissions } = formData;

      // recaptcha - ugh
      const recaptcha = (document.getElementById("g-recaptcha-response") as HTMLTextAreaElement)
        ?.value;

      submissions["g-recaptcha-response"] = recaptcha;

      try {
        const formData = {
          email: user?.email ?? "Guest",
          ...submissions,
          metadata: {
            previewMode,
          },
        };

        await postFormAndFiles(files, formData, FormNames.LeadGen);
        setShowExitDialog({ show: false, seen: true });
        router.push("lead-gen/thanks");
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 400) {
          // upload error
          const message = extractErrorMessage(err, DEFAULT_MSG);
          toast({
            title: DEFAULT_MSG,
            description: message,
            variant: "error",
            duration: 10e3,
            figure: <IconAlertExclamationFill className="w-32 h-32" />,
          });
        }
      }
    },
    [files, postFormAndFiles, previewMode, router, setShowExitDialog, toast, user?.email]
  );

  const { mutateAsync, isPending: isSubmitting } = useMutation({
    mutationFn: handleSubmit,
  });

  const submitForm = useCallback(
    ({ value }: { value: Record<string, string | undefined> }) => {
      mutateAsync(value);
    },
    [mutateAsync]
  );

  const leadForm = useForm({
    defaultValues,
    onSubmit: submitForm,
    validatorAdapter: zodValidator,
  });

  const exitDialogSeen = showExitDialog?.seen ?? false;

  const getValidator = (field: SanityFormTextField) => {
    if (!field.isRequired) return () => null;

    return field.dataType === "email"
      ? z.string().email({ message: "Invalid email address" })
      : z
          .string({
            required_error: `${field.label} is required`,
          })
          .trim()
          .min(1, `${TEXT_ERROR} for ${field.label}.`);
  };

  const renderTextField = (field: SanityFormTextField, className: string = "col-span-2") => {
    return (
      <leadForm.Field
        key={field.id}
        name={field.id as LeadGenField}
        validators={{
          onChange: getValidator(field),
        }}
        children={_field => (
          <SanityFormText
            key={field._key}
            field={field}
            defaultValue={defaultValues?.[field.id as LeadGenField & UtmParam]}
            className={className}
            fieldMeta={_field.state?.meta}
            onChange={v => {
              _field.handleChange(v);
            }}
          />
        )}
      />
    );
  };

  const renderSelectField = (field: SanityFormDropdownField, className: string = "col-span-2") => {
    return (
      <leadForm.Field
        key={field.id}
        name={field.id as LeadGenField}
        validators={{
          onChange: z.string({
            required_error: `${field.label} is required`,
          }),
        }}
        children={_field => (
          <SanityFormDropdown
            key={field._key}
            field={field}
            defaultValue={defaultValues[field.id as LeadGenField & UtmParam]}
            className={className}
            fieldMeta={_field.state?.meta}
            onChange={v => {
              _field.handleChange(v);
            }}
          />
        )}
      />
    );
  };

  useEffect(() => {
    if (exitDialogSeen) {
      return;
    }
    setShowExitDialog({ show: true, seen: false });
  }, [setShowExitDialog, exitDialogSeen]);

  return (
    <form
      className="p-24 shadow-hi rounded-md bg-ui-lightest lg:-mt-[232px] lg:p-48"
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        void leadForm.handleSubmit();
      }}
    >
      <input type="hidden" name="product-id" value={productDetails.product.baseProductId} />
      <input type="hidden" name="product-version-id" value={productDetails.product.versionId} />
      <input type="hidden" name="product" value={productDetails.product.name} />
      <input type="hidden" name="preset" value={productDetails?.preset?.name} />
      <input type="hidden" name="quantity" value={productDetails.quantity} />
      <input type="hidden" name="size" value={productDetails.size} />
      <input type="hidden" name="custom-size" value={isCustomSize ? "yes" : "no"} />

      <fieldset disabled={isSubmitting} className="flex flex-col gap-24 md:gap-48">
        <TextTitle size="small" asChild>
          <legend className="mb-24 md:mb-48">{form.title}</legend>
        </TextTitle>
        <div className="grid grid-cols-2 gap-16">
          {form.fields?.map((field: any) => {
            switch (field._type) {
              case sanityFieldTypes.GROUP:
                return field.fields?.map((subField: any) => {
                  // eslint-disable-next-line sonarjs/no-nested-switch
                  switch (subField._type) {
                    case "formText":
                      return renderTextField(subField, "col-span-2 md:col-span-1");
                    case "formDropdown":
                      return renderSelectField(subField, "col-span-2 md:col-span-1");
                    default:
                      return null;
                  }
                });
              case sanityFieldTypes.TEXT:
                return renderTextField(field);
              case sanityFieldTypes.LIST:
                return renderSelectField(field);
              default:
                return null;
            }
          })}
        </div>

        <LeadGenFormFileUploader
          heading={form.fileUploadHeading}
          subheading={form.fileUploadText}
          onFiles={setFiles}
        />

        <div className="flex flex-col flex-nowrap gap-20">
          <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY}></div>
          <leadForm.Field
            key="consent"
            name={"consent" as LeadGenField}
            validators={{
              onChange: z.boolean({
                required_error: `${form.consentText} is required`,
              }),
            }}
            children={_field => (
              <LeadGenConsentCheckbox
                consentText={form.consentText}
                fieldMeta={_field.state?.meta}
                onChange={v => {
                  _field.handleChange(v);
                }}
              />
            )}
          />

          <Button type="submit" variant="primary" size="md" className="self-start">
            {isSubmitting ? "Submitting..." : form.submitButtonText}
          </Button>

          {isSubmitting && <TextBody size="small">Please, wait...</TextBody>}
        </div>
      </fieldset>

      {paramsWithValues.map(([param, value]) => (
        <input key={param} type="hidden" name={param} value={value} />
      ))}
    </form>
  );
}
